import { Column } from '@access-erp/common';
import { CellClickedEvent, CellType, FilterValueTypes } from '@access-erp/ui-components';
import { ExternalLink } from 'lucide-react';

import {
  createLinkCellRenderer,
  shouldSkipCellClickNavigation
} from '@/components/common/EnquiriesScreen/components/LinkCellRenderer';
import { CellFormatters } from '@/components/common/EnquiriesScreen/utils/CellFormatters';
import { guidFilterOptions } from '@/components/common/EnquiriesScreen/utils/filters.config';
import { extractSubMenuStatusFilters } from './filters.config';
import { filterBillingCycleStatuses } from '@/components/common/EnquiriesScreen/utils/statuses.config';
import { browserHistory } from '@/routing/browserHistory';
import { getParameterizedLink } from '@/utils/getLinkWithSubscriptionId.utils';
import { billingBCPath } from '@/utils/path.utlis';
import { BillingCycleOutput } from '@/graphql/contracts/_generated_/contracts';

export interface UseTransactionTableColumnsReturn {
  headerColumns: Column<unknown>[];
}

type DefaultColumnArgsType = {
  getContractLink: (contractId: string, searchString?: string) => string;
  goToContract: (contractLink: string) => void;
};


export const getHeaderColumns = (
  { getContractLink, goToContract }: DefaultColumnArgsType
): Column<BillingCycleOutput>[] => {
  let filterIndex = 1;
  const nextFilter = () => filterIndex++;

  return [
    {
      name: 'contractNumber',
      caption: 'Contract Number',
      width: 200,
      cellType: CellType.String,
      cellRenderer: createLinkCellRenderer<BillingCycleOutput>(({ data, searchString }) => {
        const contractId = data?.contractID ?? '';
        if (!contractId) {
          return '#';
        }
        return getContractLink(contractId, searchString);
      }),
      onCellClicked: (e: CellClickedEvent) => {
        if (shouldSkipCellClickNavigation(e)) {
          return;
        }
        const searchStringIfAny = e.context?.serverSide?.current?.search;
        const contractId = e.data.contractID ?? '';
        goToContract(getContractLink(contractId, searchStringIfAny));
      },
      defaultFilter: nextFilter(),
    },
    {
      name: 'startDate',
      caption: 'Start Date',
      width: 150,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      defaultFilter: nextFilter(),
    },
    {
      name: 'endDate',
      caption: 'End Date',
      width: 150,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      defaultFilter: nextFilter(),
    },
    {
      name: 'billingDate',
      caption: 'Billing Date',
      width: 150,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      defaultFilter: nextFilter(),
    },
    {
      name: 'processingStatus',
      caption: 'Processing Status',
      width: 150,
      cellType: CellType.String,
      filterType: FilterValueTypes.EnumFilter,
      filterData: filterBillingCycleStatuses,
      defaultFilter: nextFilter(),
    },
    {
      name: 'statusReason',
      caption: 'Status Reason',
      width: 200,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'billingCustomerAccountNumber',
      caption: 'Billing Account',
      width: 130,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'billingCustomerAccountName',
      caption: 'Billing Account Name',
      width: 180,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'stockName',
      caption: 'Stock Name',
      width: 250,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'stockCode',
      caption: 'Stock Code',
      width: 150,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'quantity',
      caption: 'Quantity',
      width: 150,
      cellStyle: { justifyContent: 'end' },
      cellType: CellType.Integer,
      defaultFilter: nextFilter(),
    },
    {
      name: 'price',
      caption: 'Price',
      width: 150,
      cellStyle: { justifyContent: 'end' },
      cellType: CellType.String,
      defaultFilter: nextFilter(),
      filterType: FilterValueTypes.StringFilter,
    },
    {
      name: 'net',
      caption: 'Net',
      width: 150,
      cellStyle: { justifyContent: 'end' },
      cellType: CellType.String,
      defaultFilter: nextFilter(),
      filterType: FilterValueTypes.StringFilter,
    },
    {
      name: 'salesOrderNumber',
      caption: 'SO Number',
      width: 200,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'salesOrderStatus',
      caption: 'SO Status',
      cellType: CellType.String,
      filterType: FilterValueTypes.EnumFilter,
      filterData: () => extractSubMenuStatusFilters(),
      defaultFilter: nextFilter(),
    },
    {
      name: 'soCount',
      caption: 'SO Count',
      width: 120,
      cellType: CellType.Integer,
      defaultFilter: nextFilter(),
      cellRenderer: ({ value, data }) => {
        const soCount = value ?? 0;
        const billingCycleId = data?.billingCycleID;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>{soCount}</span>
            {soCount > 0 && billingCycleId && (
              <ExternalLink style={{ width: '14px', height: '14px' }} />
            )}
          </div>
        );
      },
      onCellClicked: (e: CellClickedEvent) => {
        const soCount = e.data?.soCount ?? 0;
        const billingCycleId = e.data?.billingCycleID;
        if (soCount > 0 && billingCycleId) {
          const link = `/${billingBCPath}?billingCycleId=${billingCycleId}`;
          browserHistory.push(getParameterizedLink(link));
        }
      },
    },
    {
      name: 'contractLineID',
      caption: 'Contract Line ID',
      width: 270,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
      hidden: true,
      filterType: FilterValueTypes.StringFilter,
      filterOptions: {
        availableOptions: guidFilterOptions,
      }
    },
    {
      name: 'consolidationCriteriaCode',
      caption: 'Consolidation Criteria Code',
      width: 510,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
      hidden: true,
    },
    {
      name: 'billingValue',
      caption: 'Billing Value',
      width: 150,
      cellStyle: { justifyContent: 'end' },
      cellType: CellType.Decimal,
      defaultFilter: nextFilter(),
      filterable: false,
      groupable: false,
      sortable: false,
      disableRowGroup: true,
    },
    {
      name: 'currency',
      caption: 'Currency',
      width: 100,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'billingCycleID',
      caption: 'ID',
      width: 270,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
      hidden: true,
      filterType: FilterValueTypes.StringFilter,
      filterOptions: {
        availableOptions: guidFilterOptions,
      }
    },
  ];
};
